import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { getCurrentStaff, StaffUser } from "@/Calls/callStaffAuth";
import { Package, ShoppingCart, Wrench, CheckCircle, ArrowRight, Loader2 } from "lucide-react";
import { API_URL } from "@/config";
import { Card } from "@/components/ui/card";

interface ShopStats {
    open: number;
    inProgress: number;
    completedToday: number;
    apptToday: number;
}

interface RepairOrder {
    ro_id: number;
    status: string;
    vehicle_desc: string | null;
    parts_items: unknown;
    appointment: { customer_name: string | null; service_type: string | null } | null;
}

const STATUS_COLOR: Record<string, string> = {
    OPEN:        "bg-blue-100 text-blue-700",
    IN_PROGRESS: "bg-orange-100 text-orange-700",
    COMPLETED:   "bg-teal-100 text-teal-700",
    INVOICED:    "bg-violet-100 text-violet-700",
    PAID:        "bg-green-100 text-green-700",
};

function countParts(parts_items: unknown): number {
    if (!Array.isArray(parts_items)) return 0;
    return parts_items.reduce((sum: number, p: { qty?: number }) => sum + (Number(p?.qty) || 1), 0);
}

export default function PartsDashboard() {
    const [staff,   setStaff]   = useState<StaffUser | null>(null);
    const [stats,   setStats]   = useState<ShopStats | null>(null);
    const [orders,  setOrders]  = useState<RepairOrder[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            getCurrentStaff().then((r) => { if (r.ok && r.staff) setStaff(r.staff); }),
            axios.get(`${API_URL}/repair-shops/staff/stats`, { withCredentials: true })
                .then((r) => setStats(r.data.stats ?? null))
                .catch(() => null),
            axios.get(`${API_URL}/repair-shops/staff/repair-orders`, { withCredentials: true })
                .then((r) => setOrders(r.data.repair_orders ?? []))
                .catch(() => null),
        ]).finally(() => setLoading(false));
    }, []);

    const rosWithParts   = orders.filter((ro) => Array.isArray(ro.parts_items) && (ro.parts_items as unknown[]).length > 0);
    const totalPartsOpen = rosWithParts
        .filter((ro) => ["OPEN", "IN_PROGRESS"].includes(ro.status))
        .reduce((sum, ro) => sum + countParts(ro.parts_items), 0);

    const statCards = [
        { label: "Open Jobs",        value: stats?.open ?? 0,     icon: ShoppingCart, color: "text-blue-500",   bg: "bg-blue-50"   },
        { label: "In Progress",      value: stats?.inProgress ?? 0, icon: Wrench,      color: "text-orange-500", bg: "bg-orange-50" },
        { label: "Parts Requested",  value: totalPartsOpen,        icon: Package,      color: "text-yellow-500", bg: "bg-yellow-50" },
        { label: "Closed Today",     value: stats?.completedToday ?? 0, icon: CheckCircle, color: "text-green-500", bg: "bg-green-50" },
    ];

    const recentWithParts = rosWithParts.slice(0, 6);

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">
                    {staff ? `Welcome, ${staff.first_name}` : "Parts Dashboard"}
                </h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                    {staff ? `Parts · ${staff.role.replace(/_/g, " ")}` : "Loading…"}
                </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {statCards.map(({ label, value, icon: Icon, color, bg }) => (
                    <Card key={label} className="p-4">
                        <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center mb-3`}>
                            <Icon className={`h-5 w-5 ${color}`} />
                        </div>
                        <p className="text-2xl font-bold">
                            {loading ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : value}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
                    </Card>
                ))}
            </div>

            <div className="flex flex-wrap gap-3">
                <Link
                    to="/parts/repair-orders"
                    className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium hover:bg-muted transition"
                >
                    <Package className="h-4 w-4" /> All Repair Orders
                </Link>
            </div>

            <Card className="overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b">
                    <h2 className="font-semibold">Jobs with Parts</h2>
                    <Link
                        to="/parts/repair-orders"
                        className="text-xs text-yellow-600 hover:underline flex items-center gap-1"
                    >
                        View all <ArrowRight className="h-3 w-3" />
                    </Link>
                </div>

                {loading ? (
                    <div className="flex justify-center py-10">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                ) : recentWithParts.length === 0 ? (
                    <div className="py-10 text-center text-sm text-muted-foreground">No repair orders with parts yet</div>
                ) : (
                    <div className="divide-y">
                        {recentWithParts.map((ro) => {
                            const partCount = countParts(ro.parts_items);
                            return (
                                <Link
                                    key={ro.ro_id}
                                    to={`/parts/repair-orders/${ro.ro_id}`}
                                    className="flex items-center justify-between px-5 py-3 hover:bg-muted/40 transition-colors"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-yellow-50 flex items-center justify-center">
                                            <Package className="h-4 w-4 text-yellow-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium">RO #{ro.ro_id}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {ro.vehicle_desc ?? ro.appointment?.service_type ?? "—"} · {partCount} part{partCount !== 1 ? "s" : ""}
                                            </p>
                                        </div>
                                    </div>
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLOR[ro.status] ?? "bg-gray-100 text-gray-600"}`}>
                                        {ro.status.replace("_", " ")}
                                    </span>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </Card>
        </div>
    );
}
